import type { ChatMessageType } from "@metalmind-ab/mmagent-chat-ui";

export type MyVaultStreamOutcome = "completed" | "failed";

export interface MyVaultSendIdentity {
  generation: number;
  contextVersion: number;
  workspaceId: string;
  assistantMessageCreatedAt: Date | null;
}

export interface MyVaultSendContext {
  generation: number;
  contextVersion: number;
  workspaceId: string;
}

export interface MyVaultSendSettlement {
  messages: ChatMessageType[];
  clearLoading: boolean;
}

/** Apply completion only to the send that still owns the visible chat context. */
export function settleMyVaultSend(
  messages: ChatMessageType[],
  identity: MyVaultSendIdentity,
  current: MyVaultSendContext,
  outcome: MyVaultStreamOutcome,
): MyVaultSendSettlement {
  if (
    identity.generation !== current.generation ||
    identity.contextVersion !== current.contextVersion ||
    identity.workspaceId !== current.workspaceId
  ) {
    return { messages, clearLoading: false };
  }

  return {
    messages: identity.assistantMessageCreatedAt
      ? messages.map((message) =>
          message.role === "assistant" &&
          message.createdAt === identity.assistantMessageCreatedAt
            ? finalizeMyVaultAssistantMessage(message, outcome)
            : message,
        )
      : messages,
    clearLoading: true,
  };
}

/**
 * Settle any live assistant state after the MyVault send promise settles.
 * A tool may still be marked running when the chat package stops reading at
 * a terminal frame before a trailing tool_end frame is dispatched.
 */
export function finalizeMyVaultAssistantMessage(
  message: ChatMessageType,
  outcome: MyVaultStreamOutcome,
  failureMessage = "The tool did not finish before the chat stream failed.",
): ChatMessageType {
  if (message.role !== "assistant") return message;

  const toolExecutions = message.toolExecutions?.map((execution) => {
    if (execution.status !== "running") return execution;
    return outcome === "completed"
      ? { ...execution, status: "completed" as const }
      : { ...execution, status: "error" as const, error: failureMessage };
  });

  return {
    ...message,
    isStreaming: false,
    isWorking: false,
    ...(toolExecutions ? { toolExecutions } : {}),
  };
}
